<!--
    Notification button with dropdown menu (Feature component)

    <NotificationMenu />
-->
<script setup lang="ts">
/* VUE */
/* COMPONENTS */
import { useQueryClient } from '@tanstack/vue-query';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useCssBreakpoints, ALIconButton  } from '@af-lib/vue-ui';

/* PUSHER */
import PusherClient from '@/api/clients/pusher/PusherClient';
/* HELPERS */
import { flatMapIt } from '@/api/helpers/helpers';
import {
  useDeleteNotification,
  useUpdateAllNotifications
} from '@/api/vue-query/notifications/notifications.mutations';
/* VUE QUERY */
import { useGetNotifications } from '@/api/vue-query/notifications/notifications.queries';
import NotificationEmptyState from '@/components/notifications/NotificationEmptyState.vue';
import NotificationHeader from '@/components/notifications/NotificationHeader.vue';
import { getNotifConfig } from '@/components/notifications/notificationLayout.config';
/* CONFIG */
import NotificationList from '@/components/notifications/NotificationList.vue';
import NotificationSkeleton from '@/components/notifications/NotificationSkeleton.vue';
import { t } from '@/config/i18n';
/* STORES */
import { useAuthStore } from '@/stores/auth';
/* TYPES */
import type { MeNotificationsResponse } from '@/types/api/me/me';

/* UTILS */
const pusherClient = PusherClient();
const auth = useAuthStore().getFullUser;
const { tabletAndUp } = useCssBreakpoints();
const queryClient = useQueryClient();
const CHANNEL_NAME = `private-users.notifications.${auth.uuid}`;

/* REF */
const isMenuOpen = ref(false);
const notificationList = computed(() => (notifsResponse.value?.pages ? flatMapIt(notifsResponse.value?.pages) : []));
const notificationMeta = computed<MeNotificationsResponse['meta']>(() => notifsResponse.value?.pages?.[0]?.meta);
const hasNewNotif = computed(() => !!notifsResponse.value?.pages?.[0]?.meta?.nb_unread);

/* COMPUTED */
const containerNotifMenu = computed(() => {
  return tabletAndUp.value ? 'v-menu' : 'v-dialog';
});

const { mutate: updateAllNotifs } = useUpdateAllNotifications();
const { mutate: deleteNotif } = useDeleteNotification();

const { data: notifsResponse, isLoading: notificationLoading, fetchNextPage, hasNextPage } = useGetNotifications();

/* CONST */
const containerAttributes = {
  'v-menu': {
    offset: '11',
    location: 'bottom center',
    width: '460'
  },
  'v-dialog': {
    fullscreen: true
  }
};

/* FUNCTIONS */
/**
 * Function called on setup allowing pusher to update in real time
 */
function useRealTimeNotifications() {
  const channel = pusherClient.subscribeToPusherChannel(CHANNEL_NAME);

  function onNewNotification(newNotification) {
    queryClient.invalidateQueries({ queryKey: ['getMeNotifications'] }); // check the page does not go blank

    // If queries need to be invalidated, invalidate them
    const { invalidates, onPusherEvent } = getNotifConfig(newNotification.type);
    if (invalidates) {
      const invalidations = invalidates(newNotification);
      invalidations.forEach(invalidation => {
        queryClient.invalidateQueries({ queryKey: invalidation });
      });
    }
    if (onPusherEvent) {
      onPusherEvent(newNotification, deleteNotif);
    }
  }

  // bind to all events on the channel
  channel.bind('notification_created', onNewNotification);

  onBeforeUnmount(() => {
    channel.unbind('notification_created', onNewNotification);
  });
}

/* WATCHERS */
/* When we receive the notification data */
// Handle menu open state and action with it
watch(isMenuOpen, () => {
  if (isMenuOpen.value && hasNewNotif.value) {
    // Update all unseen notification if needed
    updateAllNotifs('read');
  }
});

/* LIFECYCLE */
useRealTimeNotifications();
</script>

<template>
  <component :is="containerNotifMenu" v-model="isMenuOpen" v-bind="containerAttributes[containerNotifMenu]">
    <template #activator="{ props }">
      <v-badge v-if="hasNewNotif" v-bind="props" :offsetX="10" :offsetY="12" bordered style="cursor: pointer">
        <ALIconButton
          variant="tertiary"
          icon="fa fa-bell"
          :label="t('accessibility.notification.bell')"
          :shouldTeleportTooltip="true"
        />
      </v-badge>
      <div v-else v-bind="props">
        <ALIconButton
          variant="tertiary"
          icon="fa fa-bell"
          :label="t('accessibility.notification.bell')"
          :shouldTeleportTooltip="true"
        />
      </div>
    </template>
    <!-- Dropdown -->
    <v-sheet class="flex h-full min-h-0 flex-col overflow-y-hidden md:min-h-[352px] md:max-h-[736px]">
      <!-- Notification Header -->
      <NotificationHeader :unreadCount="notificationMeta?.nb_read" @close:menu="isMenuOpen = false" />
      <!-- Skeleton loading -->
      <NotificationSkeleton v-if="notificationLoading" />
      <!-- Notification Content  -->
      <template v-else>
        <template v-if="notificationList.length > 0">
          <!-- Notification Infinite List -->
          <NotificationList
            :notificationList="notificationList"
            :hasNextPage="hasNextPage"
            @update:page="fetchNextPage"
            @close="isMenuOpen = false"
          />
        </template>
        <!-- Empty State -->
        <NotificationEmptyState v-else />
      </template>
    </v-sheet>
  </component>
</template>
